import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useBlockSelectionStore } from '@/components/blocks/editor/block-selection';
import { isRichTextEditingPortalTarget } from '@/lib/editor-portal-targets';
import { elementFromEventTarget, getDocument } from '@/lib/utils';
import { useSelectThenEditField } from './use-select-then-edit-field';

type FieldDismiss = () => void;

const activeBySection = new Map<string, FieldDismiss>();

function activateCollectionSelectField(sectionId: string, dismiss: FieldDismiss) {
  const previous = activeBySection.get(sectionId);
  if (previous && previous !== dismiss) {
    previous();
  }
  activeBySection.set(sectionId, dismiss);
}

function deactivateCollectionSelectField(sectionId: string, dismiss: FieldDismiss) {
  if (activeBySection.get(sectionId) === dismiss) {
    activeBySection.delete(sectionId);
  }
}

export function clearCollectionSelectFields() {
  activeBySection.clear();
}

function isInsideCollectionCard(target: EventTarget | null): boolean {
  return Boolean(elementFromEventTarget(target)?.closest('[data-collection-card-editor-root]'));
}

function resolveOwningCollectionBlockId(element: HTMLElement | null): string | null {
  if (!element) {
    return null;
  }
  const cardRoot = element.closest('[data-collection-card-editor-root]');
  if (!cardRoot) {
    return null;
  }
  const collectionRoot = cardRoot.closest('[data-block-selection-root]');
  const blockId = collectionRoot?.getAttribute('data-block-selection-root');
  return blockId || null;
}

export function useCollectionSelectThenEditField(collectionSectionId: string | undefined) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dismissBridgeRef = useRef<(outsideTarget?: EventTarget | null) => void>(() => undefined);
  const stableDismissRef = useRef<FieldDismiss>(() => dismissBridgeRef.current());

  const registerDismiss = useCallback((dismiss: FieldDismiss) => {
    dismissBridgeRef.current = dismiss;
  }, []);

  const getCollectionBlockContext = useCallback(() => {
    if (!collectionSectionId) {
      return null;
    }
    const collectionBlockId = resolveOwningCollectionBlockId(rootRef.current);
    if (!collectionBlockId) {
      return null;
    }
    return { sectionId: collectionSectionId, collectionBlockId };
  }, [collectionSectionId]);

  const clearCollectionActiveIfNeeded = useCallback(() => {
    const context = getCollectionBlockContext();
    if (!context) {
      return;
    }
    const { active } = useBlockSelectionStore.getState();
    if (active?.sectionId === context.sectionId && active.blockId === context.collectionBlockId) {
      useBlockSelectionStore.setState({ active: null });
    }
  }, [getCollectionBlockContext]);

  const retainCollectionToolbar = useCallback(() => {
    const context = getCollectionBlockContext();
    if (!context) {
      return;
    }
    useBlockSelectionStore.getState().setActiveBlock(context.sectionId, context.collectionBlockId);
  }, [getCollectionBlockContext]);

  const onActivate = useCallback(() => {
    if (!collectionSectionId) {
      return;
    }
    activateCollectionSelectField(collectionSectionId, stableDismissRef.current);
    clearCollectionActiveIfNeeded();
  }, [clearCollectionActiveIfNeeded, collectionSectionId]);

  const onDismissStart = useCallback(() => {
    if (!collectionSectionId) {
      return;
    }
    deactivateCollectionSelectField(collectionSectionId, stableDismissRef.current);
  }, [collectionSectionId]);

  const onDismissEnd = useCallback(
    (outsideTarget?: EventTarget | null) => {
      if (collectionSectionId && outsideTarget && isInsideCollectionCard(outsideTarget)) {
        retainCollectionToolbar();
      }
    },
    [collectionSectionId, retainCollectionToolbar],
  );

  const onEditorFocusChange = useCallback(
    (_focused: boolean, dismiss: (outsideTarget?: EventTarget | null) => void) => {
      if (!collectionSectionId) {
        dismiss();
        return;
      }

      queueMicrotask(() => {
        const active = getDocument()?.activeElement ?? null;
        if (active && rootRef.current?.contains(active)) {
          return;
        }
        if (isRichTextEditingPortalTarget(active)) {
          return;
        }
        dismiss(active);
      });
    },
    [collectionSectionId],
  );

  const selectThenEditOptions = useMemo(
    () => ({
      rootRef,
      dismissOnEditorBlur: false,
      onActivate,
      onDismissStart,
      onDismissEnd,
      onEditorFocusChange,
      registerDismiss,
    }),
    [onActivate, onDismissEnd, onDismissStart, onEditorFocusChange, registerDismiss],
  );

  const field = useSelectThenEditField(selectThenEditOptions);

  useEffect(() => {
    if (!field.isEditing || !collectionSectionId) {
      return;
    }
    useBlockSelectionStore.getState().setCollectionCardRichTextFocus(true, collectionSectionId);
    return () => {
      useBlockSelectionStore.getState().setCollectionCardRichTextFocus(false);
    };
  }, [collectionSectionId, field.isEditing]);

  useEffect(() => {
    return () => {
      if (collectionSectionId) {
        deactivateCollectionSelectField(collectionSectionId, stableDismissRef.current);
      }
    };
  }, [collectionSectionId]);

  return field;
}
